import { useState, useRef, useEffect } from 'react';
import { X, Plus, Tag, Hash } from 'lucide-react';
import { useTagManagement } from '../../hooks/useTagManagement';


interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  readingText?: string; // For AI suggestions
  className?: string;
  maxTags?: number;
}

export default function TagInput({
  value = [],
  onChange,
  placeholder = "Add tags...",
  suggestions = [],
  readingText = '',
  className = '',
  maxTags = 10
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { tags, suggestTags, createTag } = useTagManagement();

  // Get suggestions based on input and reading text
  const allSuggestions = [
    ...suggestions,
    ...tags.filter(tag => 
      tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.includes(tag.name)
    ).map(tag => tag.name),
    ...(readingText ? suggestTags(readingText).map(tag => tag.name) : [])
  ].filter((tag, index, arr) => arr.indexOf(tag) === index); // Remove duplicates

  const filteredSuggestions = inputValue.trim() 
    ? allSuggestions.filter(tag => 
        tag.toLowerCase().includes(inputValue.toLowerCase()) &&
        !value.includes(tag)
      ).slice(0, 8)
    : allSuggestions.slice(0, 8);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(newValue.length > 0 || filteredSuggestions.length > 0);
    setFocusedIndex(-1);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
        addTag(filteredSuggestions[focusedIndex]);
      } else if (inputValue.trim()) {
        addTag(inputValue.trim().toLowerCase());
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => 
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setFocusedIndex(-1);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag if input is empty
      removeTag(value[value.length - 1]);
    } else if (e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue.trim().toLowerCase());
      }
    }
  };

  const addTag = (tag: string) => {
    const normalizedTag = tag.toLowerCase().trim();
    if (normalizedTag && !value.includes(normalizedTag) && value.length < maxTags) {
      // Create tag if it doesn't exist
      const existingTag = tags.find(t => t.name === normalizedTag);
      if (!existingTag) {
        createTag({
          name: normalizedTag,
          color: getRandomColor(),
          description: undefined
        });
      }
      
      onChange([...value, normalizedTag]);
      setInputValue('');
      setShowSuggestions(false);
      setFocusedIndex(-1);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  const getRandomColor = () => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
      '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
      '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
      '#ec4899', '#f43f5e'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getTagColor = (tagName: string) => {
    const tag = tags.find(t => t.name === tagName);
    return tag?.color || '#6b7280';
  };

  useEffect(() => {
    if (showSuggestions && filteredSuggestions.length === 0) {
      setShowSuggestions(false);
    }
  }, [filteredSuggestions.length, showSuggestions]);

  return (
    <div className={`relative ${className}`}>
      {/* Tags Display */}
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full transition-colors"
            style={{ 
              backgroundColor: `${getTagColor(tag)}20`,
              color: getTagColor(tag)
            }}
          >
            <Hash className="h-3 w-3" />
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="ml-1 hover:bg-black/20 rounded-full p-0.5 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Input */}
      <div className="relative">
        <div className="flex items-center">
          <Tag className="absolute left-3 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setShowSuggestions(filteredSuggestions.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={value.length >= maxTags ? `Max ${maxTags} tags` : placeholder}
            disabled={value.length >= maxTags}
            className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => {
              const existingTag = tags.find(t => t.name === suggestion);
              const isNew = !existingTag;
              
              return (
                <button
                  key={suggestion}
                  onClick={() => addTag(suggestion)}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2 ${
                    index === focusedIndex
                      ? 'bg-primary-500/20 text-primary-400'
                      : 'text-gray-300 hover:bg-dark-700'
                  }`}
                >
                  {isNew ? (
                    <>
                      <Plus className="h-3 w-3 text-green-400" />
                      <span>Create "{suggestion}"</span>
                    </>
                  ) : (
                    <>
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: existingTag.color }}
                      />
                      <span>{suggestion}</span>
                      {existingTag.usageCount > 0 && (
                        <span className="ml-auto text-xs text-gray-500">
                          {existingTag.usageCount}
                        </span>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-1 text-xs text-gray-500">
        {value.length}/{maxTags} tags • Press Enter, comma, or space to add
      </div>
    </div>
  );
}